/**
 * /chat 대화 속 퀴즈(```wq-quiz)·플래시카드(```wq-flashcards) 펜스 해석.
 *
 * 모델은 펜스 안에 한 줄에 JSON 하나를 쓴다(백엔드 services/chat_inchat_quiz_service.py 안내).
 *   ```wq-quiz
 *   {"title":"광합성 확인 퀴즈"}
 *   {"q":"…","choices":["…","…"],"answer":"정답 보기 글","explain":"…"}
 *   {"type":"ox","q":"…","answer":"O","explain":"…"}
 *   {"type":"short","q":"…","answer":"녹말","accept":["전분"],"explain":"…"}
 *   ```
 *   ```wq-flashcards
 *   {"title":"임진왜란 낱말"}
 *   {"front":"한산도 대첩","back":"1592년 이순신이 학익진으로 이긴 해전"}
 *   ```
 * 한 줄 JSON 이 아니어도(여러 줄로 펼친 객체, 배열, {"items":[…]}) 객체 단위로 읽는다. 깨진 문항은 빼고 나머지를 쓴다.
 * 화면은 풀이·카드로 그리고, 복사·읽어 주기·익명 기록처럼 글만 다루는 곳은 문제 → 정답과 해설 순서의 글로 바꾼다.
 */

export const QUIZ_FENCE_LANG = 'wq-quiz';
export const FLASHCARDS_FENCE_LANG = 'wq-flashcards';

export const QUIZ_MAX_ITEMS = 20;
export const FLASHCARDS_MAX_CARDS = 40;
const RAW_MAX_CHARS = 24000;
const MAX_OBJECTS = 80;
const TITLE_MAX = 60;
const QUESTION_MAX = 300;
const CHOICE_MAX = 120;
const CHOICES_MIN = 2;
const CHOICES_MAX = 6;
const ANSWER_MAX = 80;
const ACCEPT_MAX = 6;
const EXPLAIN_MAX = 300;
const FRONT_MAX = 120;
const BACK_MAX = 400;

export const CHOICE_MARKS = ['①', '②', '③', '④', '⑤', '⑥'];

export const isInchatQuizEnabled = () => (
  !['false', '0', 'off'].includes(String(process.env.REACT_APP_CHAT_INCHAT_QUIZ || '').trim().toLowerCase())
);

const clip = (value, max) => {
  if (value === null || value === undefined || typeof value === 'object') return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, max);
};

/** 글 속 최상위 `{…}` 객체를 차례로 꺼낸다(문자열 안 괄호는 무시). 끝이 닫히지 않은 객체는 incomplete 로 센다. */
const scanObjects = (text) => {
  const objects = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escaped = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      if (depth > 0) inString = true;
    } else if (ch === '{') {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === '}' && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        objects.push(text.slice(start, i + 1));
        if (objects.length >= MAX_OBJECTS) return { objects, incomplete: false };
      }
    }
  }
  return { objects, incomplete: depth > 0 };
};

const LIST_KEYS = ['items', 'questions', 'quiz', 'cards', 'flashcards'];

/** 펜스 안 글 → 레코드(객체) 목록. bad = JSON 으로 읽지 못한 객체 수. */
const readRecords = (raw) => {
  const text = String(raw || '').slice(0, RAW_MAX_CHARS);
  const { objects, incomplete } = scanObjects(text);
  const records = [];
  let bad = 0;
  objects.forEach((chunk) => {
    let value;
    try {
      value = JSON.parse(chunk);
    } catch (error) {
      try {
        value = JSON.parse(chunk.replace(/,\s*([}\]])/g, '$1'));
      } catch (retryError) {
        bad += 1;
        return;
      }
    }
    if (!value || typeof value !== 'object') {
      bad += 1;
      return;
    }
    const listKey = LIST_KEYS.find((key) => Array.isArray(value[key]));
    if (listKey) {
      if (value.title) records.push({ title: value.title });
      value[listKey].forEach((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) records.push(item);
      });
      return;
    }
    records.push(value);
  });
  return { records, bad, incomplete };
};

const stripChoiceMark = (text) => text
  .replace(/^(?:[①-⑩]|\(?[1-9][).]|[A-Fa-f][).]|[가-바][).])\s*/, '')
  .trim();

const normalizeForMatch = (text) => String(text ?? '')
  .toLowerCase()
  .replace(/[\s.,·:;!?'"“”‘’()[\]{}<>~\-_/]/g, '');

const CIRCLED_INDEX = { '①': 0, '②': 1, '③': 2, '④': 3, '⑤': 4, '⑥': 5 };

const resolveChoiceIndex = (answer, choices) => {
  if (typeof answer === 'number' && Number.isInteger(answer)) {
    if (answer >= 1 && answer <= choices.length) return answer - 1;
    return answer === 0 ? 0 : -1;
  }
  const text = clip(answer, CHOICE_MAX);
  if (!text) return -1;
  const target = normalizeForMatch(stripChoiceMark(text));
  const exact = choices.findIndex((choice) => normalizeForMatch(choice) === target);
  if (exact >= 0 && target) return exact;
  const first = text.charAt(0);
  if (CIRCLED_INDEX[first] !== undefined && CIRCLED_INDEX[first] < choices.length) return CIRCLED_INDEX[first];
  const numbered = text.match(/^\(?([1-6])\)?(?:번)?$/);
  if (numbered) {
    const index = Number(numbered[1]) - 1;
    return index < choices.length ? index : -1;
  }
  return -1;
};

const normalizeOx = (answer) => {
  if (answer === true) return 'O';
  if (answer === false) return 'X';
  const text = clip(answer, 10).toUpperCase();
  if (['O', 'Ｏ', '○', '◯', 'TRUE', '참', '맞음', '예'].includes(text)) return 'O';
  if (['X', 'Ｘ', '×', '✕', 'FALSE', '거짓', '틀림', '아니오'].includes(text)) return 'X';
  return null;
};

/** 레코드 하나 → 문항 { type: 'mc'|'ox'|'short', q, choices?, answerIndex?, answer?, accept?, explain }. 못 쓰면 null. */
export const normalizeQuizItem = (record) => {
  if (!record || typeof record !== 'object') return null;
  const q = clip(record.q ?? record.question, QUESTION_MAX);
  if (!q) return null;
  const explain = clip(record.explain ?? record.explanation, EXPLAIN_MAX);
  const declared = clip(record.type, 10).toLowerCase();
  const rawChoices = Array.isArray(record.choices) ? record.choices : (Array.isArray(record.options) ? record.options : null);

  if (rawChoices && declared !== 'short' && declared !== 'ox') {
    const choices = rawChoices.map((choice) => stripChoiceMark(clip(choice, CHOICE_MAX)));
    if (choices.length < CHOICES_MIN || choices.length > CHOICES_MAX || choices.some((choice) => !choice)) return null;
    if (new Set(choices.map(normalizeForMatch)).size !== choices.length) return null;
    const answerIndex = resolveChoiceIndex(record.answer, choices);
    if (answerIndex < 0) return null;
    return { type: 'mc', q, choices, answerIndex, explain };
  }

  const ox = normalizeOx(record.answer);
  if (declared === 'ox' || (ox && declared !== 'short')) {
    return ox ? { type: 'ox', q, answer: ox, explain } : null;
  }

  const answer = clip(record.answer, ANSWER_MAX);
  if (!answer) return null;
  const accept = (Array.isArray(record.accept) ? record.accept : [])
    .map((item) => clip(item, ANSWER_MAX))
    .filter(Boolean)
    .slice(0, ACCEPT_MAX);
  return { type: 'short', q, answer, accept, explain };
};

export const normalizeFlashcard = (record) => {
  if (!record || typeof record !== 'object') return null;
  const front = clip(record.front ?? record.term ?? record.word ?? record.q, FRONT_MAX);
  const back = clip(record.back ?? record.definition ?? record.meaning ?? record.answer, BACK_MAX);
  return front && back ? { front, back } : null;
};

const pickTitle = (records, fallback) => {
  const found = records.find((record) => record.title && !record.q && !record.question && !record.front && !record.term);
  return clip(found?.title, TITLE_MAX) || fallback;
};

const isMetaRecord = (record) => Boolean(record.title)
  && !record.q && !record.question && !record.front && !record.term && !record.word;

/**
 * @param {string} raw 펜스 안 글
 * @returns {{ ok: boolean, error?: string, spec: { kind: 'quiz', title: string, items: object[] }, dropped: number, incomplete: boolean }}
 *   ok 가 false 여도 spec.items 에는 읽은 문항이 들어 있다(글 대체 표시용).
 */
export function parseQuizSpec(raw) {
  const { records, bad, incomplete } = readRecords(raw);
  const body = records.filter((record) => !isMetaRecord(record));
  const items = [];
  let dropped = bad;
  body.forEach((record) => {
    const item = normalizeQuizItem(record);
    if (!item) dropped += 1;
    else if (items.length < QUIZ_MAX_ITEMS) items.push(item);
  });
  const spec = { kind: 'quiz', title: pickTitle(records, '확인 퀴즈'), items };
  if (items.length === 0) return { ok: false, error: records.length || bad ? 'items' : 'json', spec, dropped, incomplete };
  return { ok: true, spec, dropped, incomplete };
}

export function parseFlashcardsSpec(raw) {
  const { records, bad, incomplete } = readRecords(raw);
  const body = records.filter((record) => !isMetaRecord(record));
  const cards = [];
  let dropped = bad;
  body.forEach((record) => {
    const card = normalizeFlashcard(record);
    if (!card) dropped += 1;
    else if (cards.length < FLASHCARDS_MAX_CARDS) cards.push(card);
  });
  const spec = { kind: 'flashcards', title: pickTitle(records, '플래시카드'), cards };
  if (cards.length < 2) return { ok: false, error: records.length || bad ? 'cards' : 'json', spec, dropped, incomplete };
  return { ok: true, spec, dropped, incomplete };
}

/** 단답 채점: 띄어쓰기·문장부호·대소문자를 무시하고 정답이나 다른 정답 표기와 같으면 맞음. */
export const isShortAnswerCorrect = (item, input) => {
  const given = normalizeForMatch(input);
  if (!given || !item) return false;
  return [item.answer, ...(item.accept || [])].some((answer) => normalizeForMatch(answer) === given);
};

export const answerLabel = (item) => {
  if (!item) return '';
  if (item.type === 'mc') return `${CHOICE_MARKS[item.answerIndex] || ''} ${item.choices[item.answerIndex]}`.trim();
  return item.answer;
};

const TYPE_HINT = { ox: ' (O/X)', short: ' (단답)' };

const escapeMarkdown = (text) => String(text || '').replace(/([\\`*_[\]<>|#])/g, '\\$1');

/**
 * 문제 → 정답과 해설 순서의 마크다운. 한글·카톡에 붙여 넣어도 바로 쓸 수 있게 문항 번호를 글로 쓰고(목록 문법 아님),
 * 보기는 줄마다 끊는다. withAnswers=false 면 정답 묶음을 뺀다(답변을 받는 중 화면용).
 */
export function quizSpecToMarkdown(spec, { withAnswers = true } = {}) {
  const items = Array.isArray(spec?.items) ? spec.items : [];
  const lines = [`**${escapeMarkdown(spec?.title || '확인 퀴즈')}**`, ''];
  items.forEach((item, index) => {
    const head = `**${index + 1}.** ${escapeMarkdown(item.q)}${TYPE_HINT[item.type] || ''}`;
    if (item.type === 'mc') {
      lines.push([head, ...item.choices.map((choice, i) => `${CHOICE_MARKS[i]} ${escapeMarkdown(choice)}`)].join('  \n'));
    } else {
      lines.push(head);
    }
    lines.push('');
  });
  if (withAnswers && items.length > 0) {
    lines.push('**정답과 해설**', '');
    items.forEach((item, index) => {
      const explain = item.explain ? ` — ${escapeMarkdown(item.explain)}` : '';
      lines.push(`${index + 1}. ${escapeMarkdown(answerLabel(item))}${explain}`);
    });
  }
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function flashcardsSpecToMarkdown(spec) {
  const cards = Array.isArray(spec?.cards) ? spec.cards : [];
  const lines = [`**${escapeMarkdown(spec?.title || '플래시카드')}**`, ''];
  cards.forEach((card, index) => {
    lines.push(`${index + 1}. ${escapeMarkdown(card.front)} — ${escapeMarkdown(card.back)}`);
  });
  return lines.join('\n').trim();
}

const INCHAT_FENCE_RE = /^ {0,3}(`{3,}|~{3,})[ \t]*(wq-quiz|wq-flashcards)[ \t]*\n([\s\S]*?)\n {0,3}\1[ \t]*$/gm;
const OPEN_INCHAT_FENCE_RE = /^ {0,3}(?:`{3,}|~{3,})[ \t]*(wq-quiz|wq-flashcards)[ \t]*(?:\n([\s\S]*))?$/m;

/** 퀴즈·카드 펜스 하나를 글로. 읽을 문항이 없으면 빈 글. */
export const inchatFenceToMarkdown = (lang, body, { withAnswers = true } = {}) => {
  if (lang === FLASHCARDS_FENCE_LANG) {
    const parsed = parseFlashcardsSpec(body);
    return parsed.spec.cards.length ? flashcardsSpecToMarkdown(parsed.spec) : '';
  }
  const parsed = parseQuizSpec(body);
  return parsed.spec.items.length ? quizSpecToMarkdown(parsed.spec, { withAnswers }) : '';
};

/**
 * 글만 다루는 경로용(복사·읽어 주기·익명 기록): 퀴즈·카드 펜스를 문제 → 정답과 해설 글로 바꾼다.
 * 읽을 문항이 하나도 없는 펜스는 지운다(JSON 을 그대로 보이지 않게). 닫히지 않은 펜스(받는 중)는 정답 없이 문제만.
 */
export function replaceQuizFences(markdown) {
  const text = typeof markdown === 'string' ? markdown : '';
  if (!text.includes('wq-quiz') && !text.includes('wq-flashcards')) return text;
  const closed = text.replace(INCHAT_FENCE_RE, (match, fence, lang, body) => inchatFenceToMarkdown(lang, body));
  const open = OPEN_INCHAT_FENCE_RE.exec(closed);
  const result = open
    ? `${closed.slice(0, open.index)}${inchatFenceToMarkdown(open[1], open[2] || '', { withAnswers: false })}`
    : closed;
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

/** 답변에 퀴즈·카드 펜스가 있는지(수업 자료 메뉴를 숨길 때 쓴다). */
export const hasInchatQuizFence = (markdown) => /^ {0,3}(?:`{3,}|~{3,})[ \t]*wq-(?:quiz|flashcards)\b/m.test(String(markdown || ''));

// 퀴즈를 낸 답에서는 정답·해설은 풀면 화면에 나온다 — '정답 보기' 류 다음 질문 칩은 답을 미리 글로 쏟게 하므로 뺀다.
const ANSWER_REVEAL_CHIP_RE = /정답|해설|답\s*(?:만\s*)?(?:알려|보여|공개)/;

export const withoutAnswerRevealChips = (chips) => (Array.isArray(chips) ? chips : [])
  .filter((chip) => !ANSWER_REVEAL_CHIP_RE.test(String(chip?.text ?? chip ?? '')));
